import { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';

import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { useIsNonDentalOrganization } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { DropdownSelect } from '@/shared/ui';
import { OptionType as Option } from '@/shared/ui/Select/Select.types';

import { STUDY_TYPE_TO_LABEL, studyTypeMessages } from '../config';

import styles from './StudiesDropdownFilter.module.scss';

type StudiesDropdownFilterProps = {
  onChange?: (values: string[], hasTranscriptor: boolean | undefined) => void;
};

export const StudiesDropdownFilter: FC<StudiesDropdownFilterProps> = ({
  onChange,
}) => {
  const { formatMessage } = useIntl();
  const isNonDental = useIsNonDentalOrganization();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFilters = searchParams.get('studyTypes')?.split(',') || [];
  const initialTranscriptor = searchParams.get('hasTranscriptor') === 'true';

  const [filters, setFilters] = useState<string[]>(initialFilters);
  const [hasTranscriptor, setHasTranscriptor] =
    useState<boolean>(initialTranscriptor);

  const selectedQuantity = filters.length + (hasTranscriptor ? 1 : 0);

  const dentalStudyOptions = useMemo(
    (): Option[] => [
      {
        value: StudyType.StudyType_CBCT.toString(),
        label: formatMessage(studyTypeMessages.cbct),
      },
      {
        value: StudyType.StudyType_IntraoralXRay.toString(),
        label: formatMessage(studyTypeMessages.ioxRay),
      },
      {
        value: StudyType.StudyType_PanoramicXRay.toString(),
        label: formatMessage(studyTypeMessages.pano),
      },
      {
        value: StudyType.StudyType_IOS_Meshes.toString(),
        label: formatMessage(studyTypeMessages.intraoralScan),
      },
      {
        value: StudyType.StudyType_DentalPhoto.toString(),
        label: formatMessage(studyTypeMessages.dentalPhotos),
      },
    ],
    [formatMessage],
  );

  const transcriptorOption = useMemo(
    (): Option => ({
      value: 'transcriptor',
      label: formatMessage(studyTypeMessages.transcriptor),
    }),
    [formatMessage],
  );

  const options = useMemo(
    (): Option[] =>
      isNonDental
        ? [transcriptorOption]
        : [...dentalStudyOptions, transcriptorOption],
    [isNonDental, dentalStudyOptions, transcriptorOption],
  );

  const handleChange = (value: string | string[]) => {
    if (typeof value === 'string') return;

    const transcriptorSelected = value.includes('transcriptor');
    const studyTypeValues = value.filter((v) => v !== 'transcriptor');

    setHasTranscriptor(transcriptorSelected);
    setFilters(studyTypeValues);
  };

  const handleApply = (value: string | string[]) => {
    if (typeof value === 'string') return;

    const transcriptorSelected = value.includes('transcriptor');
    const studyTypeValues = value.filter((v) => v !== 'transcriptor');

    const studyLabels = studyTypeValues.map(
      (studyType) => STUDY_TYPE_TO_LABEL[studyType] || studyType,
    );

    if (transcriptorSelected) {
      studyLabels.push(formatMessage(studyTypeMessages.transcriptor));
    }

    trackEvent({
      eventName: 'PatientsPageStudiesFilterApplyButtonClicked',
      properties: {
        filter: studyLabels.join(','),
      },
    });

    if (onChange) {
      onChange(studyTypeValues, transcriptorSelected || undefined);
    }

    if (studyTypeValues.length > 0) {
      searchParams.set('studyTypes', studyTypeValues.join(','));
    } else {
      searchParams.delete('studyTypes');
    }

    if (transcriptorSelected) {
      searchParams.set('hasTranscriptor', 'true');
    } else {
      searchParams.delete('hasTranscriptor');
    }

    setSearchParams(searchParams);
  };

  const handleClear = () => {
    setFilters([]);
    setHasTranscriptor(false);
    if (onChange) {
      onChange([], undefined);
    }
    searchParams.delete('studyTypes');
    searchParams.delete('hasTranscriptor');
    setSearchParams(searchParams);
  };

  const combinedValue = useMemo(() => {
    const values = [...filters];
    if (hasTranscriptor) {
      values.push('transcriptor');
    }
    return values;
  }, [filters, hasTranscriptor]);

  return (
    <DropdownSelect
      isMulti
      value={combinedValue}
      label={formatMessage({
        id: 'patientList.studies',
        defaultMessage: 'Studies',
      })}
      options={options}
      className={styles.container}
      stylesOptions={{ listMaxHeight: 206, hideSearch: true }}
      icon="filter"
      multiValuesCount={selectedQuantity > 0 ? selectedQuantity : undefined}
      onChange={handleChange}
      onApply={handleApply}
      onClear={handleClear}
    />
  );
};
